import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  createCheckIn,
  createReview,
  finalizeCheckIn,
  uploadCheckInPhoto,
} from '@/api/endpoints/check-ins';
import { sendFriendRecommendation } from '@/api/endpoints/friend-recommendation';
import { createNote, uploadNotePhoto } from '@/api/endpoints/notes';
import { NOTES_QUERY_KEYS } from '@/api/queries/notes';
import { toast } from '@/components/ui/toast';
import { FEED_QUERY_KEYS } from '@/features/activity/hooks/use-activity';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { revokeBlobUris } from '@/utils/image-picker';
import { purgeTemporaryImageFiles } from '@/utils/image-temp-files';
import { getExtensionForMimeType } from '@/utils/mime';
import { getPhotoUploadErrorMessage } from '@/utils/photo-validation';
import { getRetryableUploadMessage } from '@/utils/upload-errors';
import Sentry from '@/utils/sentry';
import { useQueryClient } from '@tanstack/react-query';
import { router } from 'expo-router';
import { NoteEntryState } from '../../notes/types';
import { CheckInEntryState, CheckInSuccessData, PhotoAsset } from '../types';
import {
  createEmptyNoteEntry,
  type CheckInResume,
  QuickCheckInState,
  updateEntryPhotos,
  updateNoteEntryPhoto,
} from './use-check-in-form-state';
import { getDraftSaveNotice, type DraftPhoto, type SavedDraft } from './use-drafts';

/** A photo that failed to upload, with a user-facing label and reason. */
interface PhotoFailure {
  label: string;
  message: string;
}

/**
 * What is left after a submit that did not fully succeed. The user stays on
 * the screen with the per-photo errors; Retry re-runs the submit, which only
 * does the parts still missing.
 */
export interface PendingRetry {
  mode: 'check-in' | 'note';
  /** Photos that did not upload (their check-in or note exists). */
  failedPhotoCount: number;
  /** Check-ins or notes that could not be created or finalized. */
  failedItemCount: number;
}

/** Server-side progress of one check-in entry, kept across retries. */
interface CheckInProgress {
  checkInId: string;
  /** Local ids of the photos already uploaded. */
  uploadedPhotoIds: ReadonlySet<string>;
  reviewDone: boolean;
  finalized: boolean;
}

/** Server-side progress of one note entry, kept across retries. */
interface NoteProgress {
  noteId: string;
  photoUploaded: boolean;
}

interface EntryResult {
  photoFailures: PhotoFailure[];
}

/** The multipart file part for a local photo. */
function toUploadFile(photo: PhotoAsset, namePrefix: string) {
  return {
    uri: photo.uri,
    type: photo.mimeType || 'image/jpeg',
    name: `${namePrefix}-${Date.now()}${getExtensionForMimeType(photo.mimeType)}`,
    file: photo.file,
  };
}

/** Upload photos in parallel; resolves with each photo's error (undefined on success). */
async function uploadPhotosInParallel(
  photos: readonly PhotoAsset[],
  upload: (photo: PhotoAsset) => Promise<unknown>
): Promise<(unknown | undefined)[]> {
  const settled = await Promise.allSettled(photos.map(upload));
  return settled.map((result) => (result.status === 'rejected' ? result.reason ?? 'error' : undefined));
}

function plural(count: number, word: string): string {
  return `${count} ${word}${count === 1 ? '' : 's'}`;
}

/** One error toast for a submit that did not fully succeed, with a Retry action. */
function reportSubmitProblems(options: {
  itemWord: 'check-in' | 'note';
  doneVerb: 'posted' | 'saved';
  doneCount: number;
  failedItemCount: number;
  photoFailures: readonly PhotoFailure[];
  onRetry: () => void;
}) {
  const { itemWord, doneVerb, doneCount, failedItemCount, photoFailures, onRetry } = options;
  const photoLines = photoFailures.map((f) => `${f.label}: ${f.message}`);
  const title =
    failedItemCount > 0
      ? `${plural(failedItemCount, itemWord)} failed`
      : `${plural(photoFailures.length, 'photo')} failed to upload`;
  const description =
    failedItemCount > 0
      ? [`${plural(doneCount, itemWord)} ${doneVerb}.`, ...photoLines].join('\n')
      : photoLines.join('\n');
  toast.error(title, { description, action: { label: 'Retry', onPress: onRetry } });
}

/** User-facing message for a failed photo upload; 429/411 get a retryable message. */
function uploadErrorMessage(error: unknown): string {
  return getRetryableUploadMessage(error) ?? getPhotoUploadErrorMessage(error);
}

/** Progress seeded from a restored draft's resume state. */
function progressFromResume(resume: CheckInResume | undefined): CheckInProgress | undefined {
  if (!resume) return undefined;
  return {
    checkInId: resume.checkInId,
    uploadedPhotoIds: new Set(resume.uploadedPhotoIds),
    reviewDone: resume.reviewDone,
    finalized: resume.finalized,
  };
}

/** Draft photo list with per-photo details (alt text, MIME type, uploaded). */
function toDraftPhotos(photos: readonly PhotoAsset[], progress: CheckInProgress | undefined) {
  const details: DraftPhoto[] = photos.map((p) => ({
    uri: p.uri,
    ...(p.altText ? { altText: p.altText } : {}),
    ...(p.mimeType ? { mimeType: p.mimeType } : {}),
    ...(progress?.uploadedPhotoIds.has(p.id) ? { uploaded: true } : {}),
  }));
  return { photos: photos.map((p) => p.uri), photoDetails: details };
}

/**
 * The draft to store for the current form. Entries whose check-in already
 * exists carry `resume`, so restoring the draft resumes instead of posting again.
 */
function buildDraftPayload(
  state: QuickCheckInState,
  draftId: string | null,
  progressFor: (entryId: string) => CheckInProgress | undefined
) {
  const firstEntry = state.entries[0];
  return {
    id: draftId || undefined,
    checkInId: null,
    business: state.business ? { id: state.business.id, name: state.business.name } : null,
    menuItem: firstEntry?.menuItem
      ? { id: firstEntry.menuItem.id, name: firstEntry.menuItem.name }
      : null,
    ratingId: firstEntry?.ratingId ?? null,
    diningType: state.diningType,
    photos: firstEntry?.photos.map((p) => p.uri) ?? [],
    reviewContent: firstEntry?.reviewContent ?? '',
    entries: state.entries.map((e) => {
      const progress = progressFor(e.id);
      return {
        menuItem: e.menuItem ? { id: e.menuItem.id, name: e.menuItem.name } : null,
        ratingId: e.ratingId,
        ...toDraftPhotos(e.photos, progress),
        reviewContent: e.reviewContent,
        recommendTo: e.recommendTo,
        ...(progress
          ? {
              resume: {
                checkInId: progress.checkInId,
                reviewDone: progress.reviewDone,
                finalized: progress.finalized,
              },
            }
          : {}),
      };
    }),
  };
}

function withMap<K, V>(map: ReadonlyMap<K, V>, key: K, value: V): ReadonlyMap<K, V> {
  return new Map(map).set(key, value);
}

/** Replace the progress of `entryId` with a patched copy (no-op when unknown). */
function patchProgress(
  ref: React.MutableRefObject<ReadonlyMap<string, CheckInProgress>>,
  entryId: string,
  patch: Partial<CheckInProgress>
) {
  const current = ref.current.get(entryId);
  if (current) ref.current = withMap(ref.current, entryId, { ...current, ...patch });
}

export interface UseCheckInActionsOptions {
  state: QuickCheckInState;
  hasUnsavedChanges: boolean;
  canSubmit: boolean;
  canSubmitNotes: boolean;
  readyEntries: CheckInEntryState[];
  readyNoteEntries: NoteEntryState[];
  draftIdRef: React.MutableRefObject<string | null>;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSavingDraft: React.Dispatch<React.SetStateAction<boolean>>;
  setLastSavedState: React.Dispatch<React.SetStateAction<QuickCheckInState | null>>;
  setNoteEntries: React.Dispatch<React.SetStateAction<NoteEntryState[]>>;
  /**
   * Form state setter. When provided, each photo's `isUploading` /
   * `uploadError` is written back during submit so the previews show progress.
   */
  setState?: React.Dispatch<React.SetStateAction<QuickCheckInState>>;
  coords: { latitude: number; longitude: number } | null;
  saveDraft: (draft: any) => Promise<SavedDraft>;
  deleteDraft: (id: string) => Promise<void>;
  onSubmitSuccess?: (data: CheckInSuccessData) => void;
}

export function useCheckInActions({
  state,
  hasUnsavedChanges,
  canSubmit,
  canSubmitNotes,
  readyEntries,
  readyNoteEntries,
  draftIdRef,
  setIsSubmitting,
  setIsSavingDraft,
  setLastSavedState,
  setNoteEntries,
  setState,
  coords,
  saveDraft,
  deleteDraft,
  onSubmitSuccess,
}: UseCheckInActionsOptions) {
  const queryClient = useQueryClient();
  const [pendingRetry, setPendingRetry] = useState<PendingRetry | null>(null);
  const checkInProgressRef = useRef<ReadonlyMap<string, CheckInProgress>>(new Map());
  const noteProgressRef = useRef<ReadonlyMap<string, NoteProgress>>(new Map());
  // Submits and draft saves still using the picked files.
  const inFlightRef = useRef<ReadonlySet<Promise<unknown>>>(new Set());

  const trackInFlight = useCallback(<T,>(work: Promise<T>): Promise<T> => {
    inFlightRef.current = new Set(inFlightRef.current).add(work);
    const untrack = () => {
      const next = new Set(inFlightRef.current);
      next.delete(work);
      inFlightRef.current = next;
    };
    work.then(untrack, untrack);
    return work;
  }, []);

  // Leaving the screen (discard, close) drops the picked photos: clear the
  // picker/manipulator temp files, but only once a submit or draft save that
  // still reads them has settled.
  useEffect(
    () => () => {
      const pending = [...inFlightRef.current];
      if (pending.length === 0) {
        void purgeTemporaryImageFiles();
        return;
      }
      void Promise.allSettled(pending).then(() => purgeTemporaryImageFiles());
    },
    []
  );

  /** Progress of an entry: this session's, else the restored draft's resume state. */
  const progressFor = useCallback(
    (entryId: string): CheckInProgress | undefined =>
      checkInProgressRef.current.get(entryId) ?? progressFromResume(state.resume?.[entryId]),
    [state.resume]
  );

  /** Upload the photos of an entry that are not on the server yet. */
  const uploadPendingEntryPhotos = useCallback(
    async (entry: CheckInEntryState, progress: CheckInProgress): Promise<PhotoFailure[]> => {
      const pending = entry.photos.filter((photo) => !progress.uploadedPhotoIds.has(photo.id));
      if (pending.length === 0) return [];
      const pendingIds = new Set(pending.map((photo) => photo.id));
      setState?.((prev) =>
        updateEntryPhotos(prev, entry.id, (photo) =>
          pendingIds.has(photo.id) ? { isUploading: true, uploadError: undefined } : {}
        )
      );

      const { checkInId } = progress;
      const errors = await uploadPhotosInParallel(pending, (photo) =>
        uploadCheckInPhoto(checkInId, toUploadFile(photo, 'check-in-photo'), {
          altText: photo.altText,
          position: entry.photos.indexOf(photo),
        })
      );
      const messages = new Map(
        pending.map((photo, i) => [
          photo.id,
          errors[i] === undefined ? undefined : uploadErrorMessage(errors[i]),
        ])
      );
      setState?.((prev) =>
        updateEntryPhotos(prev, entry.id, (photo) =>
          pendingIds.has(photo.id)
            ? { isUploading: false, uploadError: messages.get(photo.id) }
            : {}
        )
      );

      const uploaded = pending.filter((photo) => !messages.get(photo.id)).map((p) => p.id);
      checkInProgressRef.current = withMap(checkInProgressRef.current, entry.id, {
        ...progress,
        uploadedPhotoIds: new Set([...progress.uploadedPhotoIds, ...uploaded]),
      });

      return entry.photos.flatMap((photo, i) => {
        const message = messages.get(photo.id);
        return message ? [{ label: `Photo ${i + 1} of ${entry.menuItem?.name ?? 'item'}`, message }] : [];
      });
    },
    [setState]
  );

  /**
   * Submit one check-in entry, skipping whatever an earlier attempt already
   * did (create, uploaded photos, review, finalize). Throws when the check-in
   * itself cannot be created or finalized; photo failures are returned.
   */
  const submitCheckInEntry = useCallback(
    async (entry: CheckInEntryState, businessId: string): Promise<EntryResult> => {
      let progress = progressFor(entry.id);
      if (progress && !checkInProgressRef.current.has(entry.id)) {
        checkInProgressRef.current = withMap(checkInProgressRef.current, entry.id, progress);
      }
      if (!progress) {
        Sentry.addBreadcrumb({
          category: 'check-in',
          message: 'Creating draft check-in',
          level: 'info',
          data: {
            businessId,
            menuItemId: entry.menuItem!.id,
            ratingId: entry.ratingId,
            photoCount: entry.photos.length,
            hasReview: entry.reviewContent.trim().length > 0,
          },
        });
        const checkIn = await createCheckIn({
          business_id: businessId,
          menu_item_id: entry.menuItem!.id,
          rating_id: entry.ratingId!,
          latitude: coords?.latitude,
          longitude: coords?.longitude,
          dining_type: state.diningType,
          ...(state.occurredAt ? { occurred_at: state.occurredAt } : {}),
        });
        progress = { checkInId: checkIn.id, uploadedPhotoIds: new Set(), reviewDone: false, finalized: false };
        checkInProgressRef.current = withMap(checkInProgressRef.current, entry.id, progress);
      }

      // A failed photo does not fail the check-in: it is still posted, and the
      // photo can be retried on the same check-in.
      const photoFailures = await uploadPendingEntryPhotos(entry, progress);

      if (!progress.reviewDone && entry.reviewContent.trim()) {
        try {
          await createReview(progress.checkInId, { content: entry.reviewContent.trim() });
        } catch (reviewErr) {
          console.error('Review creation failed:', reviewErr);
        }
      }
      patchProgress(checkInProgressRef, entry.id, { reviewDone: true });

      if (!progress.finalized) {
        await finalizeCheckIn(progress.checkInId);
        patchProgress(checkInProgressRef, entry.id, { finalized: true });
        for (const friend of entry.recommendTo) {
          sendFriendRecommendation({
            recipient_id: friend.id,
            menu_item_id: entry.menuItem!.id,
          }).catch((err) => {
            console.warn('[CheckIn] Recommendation failed:', err);
          });
        }
      }

      return { photoFailures };
    },
    [coords, state.diningType, state.occurredAt, uploadPendingEntryPhotos, progressFor]
  );

  /** Create the note (once) and upload its photo if it is not on the server yet. */
  const submitNoteEntry = useCallback(
    async (entry: NoteEntryState, businessId: string): Promise<EntryResult> => {
      let progress = noteProgressRef.current.get(entry.id);
      if (!progress) {
        Sentry.addBreadcrumb({
          category: 'note',
          message: 'Creating note',
          level: 'info',
          data: { businessId, menuItemId: entry.menuItem!.id, hasPhoto: entry.photo !== null },
        });
        const note = await createNote({
          business_id: businessId,
          menu_item_id: entry.menuItem!.id,
          content: entry.content.trim(),
        });
        progress = { noteId: note.id, photoUploaded: false };
        noteProgressRef.current = withMap(noteProgressRef.current, entry.id, progress);
      }

      if (!entry.photo || progress.photoUploaded) return { photoFailures: [] };
      setNoteEntries((prev) =>
        updateNoteEntryPhoto(prev, entry.id, { isUploading: true, uploadError: undefined })
      );

      const noteId = progress.noteId;
      const [error] = await uploadPhotosInParallel([entry.photo], (photo) =>
        uploadNotePhoto(noteId, toUploadFile(photo, 'note-photo'), { altText: photo.altText })
      );
      const message = error === undefined ? undefined : uploadErrorMessage(error);
      setNoteEntries((prev) =>
        updateNoteEntryPhoto(prev, entry.id, { isUploading: false, uploadError: message })
      );
      noteProgressRef.current = withMap(noteProgressRef.current, entry.id, {
        noteId,
        photoUploaded: !message,
      });

      return {
        photoFailures: message ? [{ label: `Photo of ${entry.menuItem?.name ?? 'note'}`, message }] : [],
      };
    },
    [setNoteEntries]
  );

  // Confirm before an in-progress check-in is lost to any dismissal gesture —
  // the header X, Android/predictive back, and the iOS edge-swipe. `beforeRemove`
  // covers the button/hardware paths; the guard suppresses the native swipe
  // while dirty because it would otherwise pop the screen without firing.
  const { allowNextDismiss } = useUnsavedChangesGuard({
    hasUnsavedChanges,
    title: 'Discard check-in?',
    message: "You have unsaved changes. If you leave now, they'll be lost.",
    confirmLabel: 'Discard',
  });

  const handleSaveDraft = useCallback(async () => {
    if (!hasUnsavedChanges) return;

    setIsSavingDraft(true);
    try {
      const saved = await trackInFlight(
        saveDraft(buildDraftPayload(state, draftIdRef.current, progressFor))
      );
      draftIdRef.current = saved.id;

      setLastSavedState({ ...state, entries: state.entries.map((e) => ({ ...e })) });

      const notice = getDraftSaveNotice(saved);
      if (notice) {
        toast.warning(notice.title, { description: notice.description });
      } else {
        toast.success('Draft saved');
      }
    } catch (err) {
      console.error('Failed to save draft:', err);
      toast.error('Failed to save draft', { description: 'Please try again.' });
    } finally {
      setIsSavingDraft(false);
    }
  }, [hasUnsavedChanges, state, saveDraft, draftIdRef, setIsSavingDraft, setLastSavedState, trackInFlight, progressFor]);

  // The header X just requests the dismissal; the unsaved-changes guard
  // (via `beforeRemove`) intercepts it and prompts once when the form is dirty,
  // so this stays a single source of truth for the discard confirmation.
  const handleClose = useCallback(() => {
    if (router.canDismiss()) {
      router.dismiss();
    } else {
      router.replace('/(tabs)');
    }
  }, []);

  /**
   * The picked photos are done with (everything uploaded, or the user gave up
   * on the rest): delete the draft and its copies, purge the picker files,
   * release blob: URLs, and opt out of the discard prompt.
   */
  const releaseLocalPhotos = useCallback(
    async (photoUris: readonly string[]) => {
      if (draftIdRef.current) {
        try {
          await deleteDraft(draftIdRef.current);
          draftIdRef.current = null;
        } catch (draftErr) {
          console.error('Failed to delete draft:', draftErr);
        }
      }
      void purgeTemporaryImageFiles();
      revokeBlobUris([...photoUris]);
      checkInProgressRef.current = new Map();
      noteProgressRef.current = new Map();
      setPendingRetry(null);
      allowNextDismiss();
    },
    [deleteDraft, draftIdRef, allowNextDismiss]
  );

  // Retry runs the latest submit handler, so a Retry tapped on an older toast
  // still sees the current form.
  const latestSubmitRef = useRef<{ checkIn: () => Promise<void>; note: () => Promise<void> }>({
    checkIn: async () => {},
    note: async () => {},
  });
  const handleRetry = useCallback(async () => {
    const mode = pendingRetry?.mode ?? 'check-in';
    await (mode === 'note' ? latestSubmitRef.current.note() : latestSubmitRef.current.checkIn());
  }, [pendingRetry]);

  const finishCheckIns = useCallback(
    async (entries: readonly CheckInEntryState[]) => {
      await releaseLocalPhotos(state.entries.flatMap((e) => e.photos.map((p) => p.uri)));
      if (onSubmitSuccess) {
        const firstEntry = entries[0];
        onSubmitSuccess({
          menuItemId: firstEntry.menuItem!.id,
          menuItemName: firstEntry.menuItem!.name,
          businessName: state.business!.name,
          ratingKey: firstEntry.ratingKey || 'try_it',
          coverPhotoR2Key: firstEntry.menuItem!.coverPhotoR2Key,
          checkInCount: firstEntry.menuItem!.checkInCount,
        });
        return;
      }
      router.replace('/(tabs)');
      toast.success(entries.length === 1 ? 'Check-in posted' : `${entries.length} check-ins posted`);
    },
    [releaseLocalPhotos, state.entries, state.business, onSubmitSuccess]
  );

  const runCheckInSubmit = useCallback(async () => {
    const businessId = state.business!.id;
    let failedItemCount = 0;
    const photoFailures: PhotoFailure[] = [];

    for (const entry of readyEntries) {
      try {
        const result = await submitCheckInEntry(entry, businessId);
        photoFailures.push(...result.photoFailures);
      } catch (entryErr) {
        failedItemCount++;
        Sentry.captureException(entryErr);
        console.error('Check-in entry submission failed:', entryErr);
      }
    }

    queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });

    if (failedItemCount === 0 && photoFailures.length === 0) {
      await finishCheckIns(readyEntries);
      return;
    }

    // Keep the draft, the picked files and the form: the user retries or discards.
    // A kept draft learns which check-ins exist, so restoring it later resumes
    // against them instead of posting them a second time.
    if (draftIdRef.current) {
      try {
        const saved = await saveDraft(buildDraftPayload(state, draftIdRef.current, progressFor));
        draftIdRef.current = saved.id;
      } catch (draftErr) {
        console.error('Failed to record resume state in draft:', draftErr);
      }
    }
    setPendingRetry({ mode: 'check-in', failedPhotoCount: photoFailures.length, failedItemCount });
    reportSubmitProblems({
      itemWord: 'check-in',
      doneVerb: 'posted',
      doneCount: readyEntries.length - failedItemCount,
      failedItemCount,
      photoFailures,
      onRetry: () => void latestSubmitRef.current.checkIn(),
    });
  }, [state, readyEntries, submitCheckInEntry, queryClient, finishCheckIns, draftIdRef, saveDraft, progressFor]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !state.business) return;

    setIsSubmitting(true);
    try {
      await trackInFlight(runCheckInSubmit());
    } catch (err) {
      console.error('Check-in submission failed:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to submit check-in';
      toast.error('Submission failed', { description: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmit, state.business, setIsSubmitting, trackInFlight, runCheckInSubmit]);

  const runNotesSave = useCallback(async () => {
    const businessId = state.business!.id;
    let failedItemCount = 0;
    const photoFailures: PhotoFailure[] = [];

    for (const entry of readyNoteEntries) {
      try {
        const result = await submitNoteEntry(entry, businessId);
        photoFailures.push(...result.photoFailures);
      } catch (entryErr) {
        failedItemCount++;
        Sentry.captureException(entryErr);
        console.error('Note entry save failed:', entryErr);
      }
    }

    queryClient.invalidateQueries({ queryKey: NOTES_QUERY_KEYS.all });
    queryClient.invalidateQueries({ queryKey: FEED_QUERY_KEYS.all });

    if (failedItemCount === 0 && photoFailures.length === 0) {
      const savedCount = readyNoteEntries.length;
      await releaseLocalPhotos(
        readyNoteEntries.flatMap((entry) => (entry.photo ? [entry.photo.uri] : []))
      );
      setNoteEntries([createEmptyNoteEntry()]);
      router.replace('/(tabs)');
      toast.success(savedCount === 1 ? 'Note saved' : `${savedCount} notes saved`);
      return;
    }

    setPendingRetry({ mode: 'note', failedPhotoCount: photoFailures.length, failedItemCount });
    reportSubmitProblems({
      itemWord: 'note',
      doneVerb: 'saved',
      doneCount: readyNoteEntries.length - failedItemCount,
      failedItemCount,
      photoFailures,
      onRetry: () => void latestSubmitRef.current.note(),
    });
  }, [state.business, readyNoteEntries, submitNoteEntry, queryClient, releaseLocalPhotos, setNoteEntries]);

  const handleSaveNotes = useCallback(async () => {
    if (!canSubmitNotes || !state.business) return;

    setIsSubmitting(true);
    try {
      await trackInFlight(runNotesSave());
    } catch (err) {
      console.error('Note save failed:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to save notes';
      toast.error('Save failed', { description: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  }, [canSubmitNotes, state.business, setIsSubmitting, trackInFlight, runNotesSave]);

  useEffect(() => {
    latestSubmitRef.current = { checkIn: handleSubmit, note: handleSaveNotes };
  }, [handleSubmit, handleSaveNotes]);

  /** Give up on what failed: what was posted stays posted; leave the screen. */
  const handleDiscardFailed = useCallback(async () => {
    const mode = pendingRetry?.mode;
    const uris =
      mode === 'note'
        ? readyNoteEntries.flatMap((entry) => (entry.photo ? [entry.photo.uri] : []))
        : state.entries.flatMap((e) => e.photos.map((p) => p.uri));
    await releaseLocalPhotos(uris);
    if (mode === 'note') setNoteEntries([createEmptyNoteEntry()]);
    router.replace('/(tabs)');
  }, [pendingRetry, readyNoteEntries, state.entries, releaseLocalPhotos, setNoteEntries]);

  return {
    handleSaveDraft,
    handleClose,
    handleSubmit,
    handleSaveNotes,
    pendingRetry,
    handleRetry,
    handleDiscardFailed,
  };
}
